/**
 * Canvas Floating Toolbar
 * Same chrome/infrastructure as BoardV3FloatingToolbar:
 *   4-stage height-based resize (tiny/small/medium/full)
 *   Draggable position via grip handle (persisted to localStorage)
 *   Portal-based tooltips (400ms delay)
 *   Inline styles, adaptive per stage
 */

import { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from '@xstate/store/react';
import {
  ZoomIn, ZoomOut, Maximize2, Plus, MinusCircle, RotateCw,
  GripVertical, ChevronUp, Link, Unlink, GitBranch,
  PaintBucket, Palette, Type, Spline, LayoutGrid, ALargeSmall,
  Settings2, Minus, Pencil, ArrowLeft, ArrowRight,
  Eye, Square, Undo2, Redo2, Bold, Lock, Unlock, MousePointer2,
} from 'lucide-react';
import { useCanvasStores } from '../stores/CanvasContext';
import { useSelectionCount } from '../hooks/useSelectionCount';
import { useCanvasContextMenus } from '../hooks/useCanvasContextMenus';
import { useCanvasActions } from '../hooks/useCanvasActions';
import { usePanZoomActions } from '../hooks/usePanZoom';
import { useUndoRedo } from '../hooks/useUndoRedo';
import { getHandler } from '../entities/registry';
import { ToolbarTooltip } from '../../boardv3/components/toolbar/ToolbarTooltip';
import { useCanvasToolbarResize } from './toolbar/useCanvasToolbarResize';
import { canvasToolbarStore, type ToolbarStage } from './toolbar/canvasToolbarStore';
import { useToolbarActions, type MergedToolbarAction } from '../hooks/useToolbarActions';
import Sketch from '@uiw/react-color-sketch';
import { useUsedColors } from '../hooks/useUsedColors';
import { TAILWIND_PALETTE } from '../lib/colorPaletteConstants';

// ── localStorage for position ──

const POSITION_STORAGE_KEY = 'roadmap_2026_canvas_toolbar_position';

function loadToolbarPosition(): { x: number; y: number } | null {
  try {
    const raw = localStorage.getItem(POSITION_STORAGE_KEY);
    if (!raw) return null;
    const pos = JSON.parse(raw);
    if (typeof pos.x === 'number' && typeof pos.y === 'number') return pos;
  } catch {}
  return null;
}

function saveToolbarPosition(x: number, y: number) {
  localStorage.setItem(POSITION_STORAGE_KEY, JSON.stringify({ x, y }));
}

function clearToolbarPosition() {
  localStorage.removeItem(POSITION_STORAGE_KEY);
}

// ── Style constants (same as boardv3) ──

const btnBase: React.CSSProperties = {
  height: 24,
  width: 24,
  padding: 0,
  fontSize: 10,
  fontWeight: 500,
  border: '1px solid #e5e7eb',
  borderRadius: 5,
  cursor: 'pointer',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  backgroundColor: '#f9fafb',
  color: '#374151',
  flexShrink: 0,
};

const dividerStyle: React.CSSProperties = {
  width: 1,
  height: 18,
  backgroundColor: '#e5e7eb',
  margin: '0 2px',
  flexShrink: 0,
};

// ── Adaptive sizes per stage ──

const STAGE_CONFIG: Record<ToolbarStage, {
  btnSize: number;
  iconSize: number;
  gap: number;
  px: number;
  dividerH: number;
  borderRadius: number;
  gripSize: number;
}> = {
  tiny:   { btnSize: 10, iconSize: 6,  gap: 2, px: 4,  dividerH: 8,  borderRadius: 6,  gripSize: 6  },
  small:  { btnSize: 18, iconSize: 10, gap: 3, px: 6,  dividerH: 14, borderRadius: 8,  gripSize: 8  },
  medium: { btnSize: 24, iconSize: 12, gap: 4, px: 8,  dividerH: 18, borderRadius: 10, gripSize: 10 },
  full:   { btnSize: 32, iconSize: 16, gap: 6, px: 10, dividerH: 24, borderRadius: 12, gripSize: 14 },
};

export function CanvasToolbar() {
  const { t } = useTranslation();

  // ── Canvas store access ──
  const { store, panZoomStore, uiStore } = useCanvasStores();
  const selectionCount = useSelectionCount();
  const { handleRemoveSelected, buildAddSubmenuItems } = useCanvasContextMenus();
  const { handleRotate } = useCanvasActions();
  const { zoomIn, zoomOut, fitToContent } = usePanZoomActions();
  const { undo, redo, canUndo, canRedo } = useUndoRedo();
  const mergedActions = useToolbarActions();

  // ── Toolbar resize (4-stage, height) ──
  const {
    toolbarHeight, stage, isResizing, resizeDirectionRef,
    handleResizeStartTop, handleResizeStartBottom, handleResizeDoubleClick,
  } = useCanvasToolbarResize();
  const cfg = STAGE_CONFIG[stage];

  // Adaptive button style
  const adaptBtn = useMemo((): React.CSSProperties => ({
    ...btnBase,
    height: cfg.btnSize,
    width: cfg.btnSize,
    borderRadius: Math.max(3, cfg.btnSize / 6),
  }), [cfg.btnSize]);

  // Adaptive divider
  const adaptDivider = useMemo((): React.CSSProperties => ({
    ...dividerStyle,
    height: cfg.dividerH,
    margin: `0 ${Math.max(1, cfg.gap / 2)}px`,
  }), [cfg.dividerH, cfg.gap]);

  // ── Fit to content ──
  const handleFitToContent = useCallback(() => {
    const ctx = store.getSnapshot().context;
    const raw = Object.values(ctx.nodes);
    fitToContent(raw.filter(e => getHandler(e.kind).hasOwnRect).map(e => ({ x: e.x, y: e.y })));
  }, [store, fitToContent]);

  const isDepExplorerActive = useSelector(uiStore, s => s.context.depExplorerActive);
  const isSpriteClickThrough = useSelector(uiStore, s => s.context.spriteClickThrough);

  // ── Ref-based zoom label ──
  const zoomLabelRef = useRef<HTMLSpanElement>(null);
  useEffect(() => {
    function update() {
      if (zoomLabelRef.current) {
        zoomLabelRef.current.textContent = `${Math.round(panZoomStore.getSnapshot().context.zoom * 100)}%`;
      }
    }
    update();
    const sub = panZoomStore.subscribe(update);
    return () => sub.unsubscribe();
  }, [panZoomStore]);

  // ── Position drag state ──
  const toolbarRef = useRef<HTMLElement>(null);
  const [position, setPosition] = useState<{ x: number; y: number } | null>(() => loadToolbarPosition());
  const isDragging = useRef(false);
  const dragOffset = useRef({ x: 0, y: 0 });

  // Track bottom edge during top-resize so top follows mouse, bottom stays fixed.
  const resizeBottomEdgeRef = useRef(0);
  const prevHeightRef = useRef(toolbarHeight);

  useEffect(() => {
    if (isResizing && position && resizeDirectionRef.current === 1) {
      if (prevHeightRef.current !== toolbarHeight) {
        setPosition(prev => {
          if (!prev) return prev;
          const newY = resizeBottomEdgeRef.current - toolbarHeight;
          return { x: prev.x, y: Math.max(0, newY) };
        });
      }
    }
    prevHeightRef.current = toolbarHeight;
  }, [isResizing, toolbarHeight, position, resizeDirectionRef]);

  // Wrap top-resize start to snapshot the bottom edge
  const handleResizeStartTopWrapped = useCallback((e: React.MouseEvent) => {
    if (position) {
      resizeBottomEdgeRef.current = position.y + toolbarHeight;
    }
    handleResizeStartTop(e);
  }, [position, toolbarHeight, handleResizeStartTop]);

  const clampPosition = useCallback((x: number, y: number) => {
    const el = toolbarRef.current;
    if (!el) return { x, y };
    const rect = el.getBoundingClientRect();
    return {
      x: Math.max(0, Math.min(x, window.innerWidth - rect.width)),
      y: Math.max(0, Math.min(y, window.innerHeight - rect.height)),
    };
  }, []);

  useEffect(() => {
    function handleResize() {
      setPosition(prev => prev ? clampPosition(prev.x, prev.y) : null);
    }
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [clampPosition]);

  const handleGripMouseDown = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    const el = toolbarRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    isDragging.current = true;
    dragOffset.current = { x: e.clientX - rect.left, y: e.clientY - rect.top };

    function onMouseMove(ev: MouseEvent) {
      if (!isDragging.current) return;
      setPosition(clampPosition(
        ev.clientX - dragOffset.current.x,
        ev.clientY - dragOffset.current.y,
      ));
    }
    function onMouseUp() {
      isDragging.current = false;
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
      setPosition(prev => { if (prev) saveToolbarPosition(prev.x, prev.y); return prev; });
    }
    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);
  }, [clampPosition]);

  const handleGripDoubleClick = useCallback(() => {
    setPosition(null);
    clearToolbarPosition();
  }, []);

  // Expand from tiny → medium
  const handleExpandFromTiny = useCallback(() => {
    canvasToolbarStore.send({ type: 'toolbar.setStage', stage: 'medium' });
  }, []);

  // ── Toolbar style ──
  const baseStyle: React.CSSProperties = {
    position: 'fixed',
    zIndex: 9998,
    display: 'flex',
    alignItems: 'center',
    gap: cfg.gap,
    height: toolbarHeight,
    padding: `0 ${cfg.px}px`,
    backgroundColor: '#fff',
    borderRadius: cfg.borderRadius,
    boxShadow: '0 4px 16px rgba(0,0,0,0.12)',
    border: '1px solid #e5e7eb',
    userSelect: 'none' as const,
  };

  const toolbarStyle: React.CSSProperties = position
    ? { ...baseStyle, left: position.x, top: position.y }
    : { ...baseStyle, bottom: 24, left: '50%', transform: 'translateX(-50%)' };

  // ── Tiny mode: grip + expand ──
  if (stage === 'tiny') {
    const tinyStyle: React.CSSProperties = position
      ? { position: 'fixed', left: position.x, top: position.y, zIndex: 9998 }
      : { position: 'fixed', bottom: 24, left: '50%', transform: 'translateX(-50%)', zIndex: 9998 };

    return (
      <div
        ref={toolbarRef as React.RefObject<HTMLDivElement | null>}
        style={{
          ...tinyStyle,
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          height: 14,
          padding: '0 3px',
          border: '1px solid #e5e7eb',
          borderRadius: 7,
          backgroundColor: '#fff',
          boxShadow: '0 2px 8px rgba(0,0,0,0.10)',
          userSelect: 'none' as const,
        }}
      >
        <div
          onMouseDown={handleGripMouseDown}
          onDoubleClick={handleGripDoubleClick}
          style={{
            cursor: 'grab',
            display: 'flex',
            alignItems: 'center',
            color: '#9ca3af',
          }}
          title={t('canvasToolbar.moveToolbar')}
        >
          <GripVertical size={6} />
        </div>
        <button
          onClick={handleExpandFromTiny}
          style={{
            padding: 0,
            border: 'none',
            backgroundColor: 'transparent',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            color: '#9ca3af',
          }}
          title={t('canvasToolbar.openToolbar')}
        >
          <ChevronUp size={8} />
        </button>
      </div>
    );
  }

  return (
    <div ref={toolbarRef as React.RefObject<HTMLDivElement | null>} style={toolbarStyle}>
      {/* ── Resize handle (top edge) ── */}
      <div
        onMouseDown={handleResizeStartTopWrapped}
        onDoubleClick={handleResizeDoubleClick}
        style={{
          position: 'absolute',
          left: 0,
          right: 0,
          top: 0,
          height: 5,
          cursor: 'row-resize',
          backgroundColor: isResizing && resizeDirectionRef.current === 1 ? '#3b82f6' : 'transparent',
          borderRadius: `${cfg.borderRadius}px ${cfg.borderRadius}px 0 0`,
          transition: isResizing ? 'none' : 'background-color 0.15s',
        }}
      />

      {/* ── Resize handle (bottom edge) ── */}
      <div
        onMouseDown={handleResizeStartBottom}
        onDoubleClick={handleResizeDoubleClick}
        style={{
          position: 'absolute',
          left: 0,
          right: 0,
          bottom: 0,
          height: 5,
          cursor: 'row-resize',
          backgroundColor: isResizing && resizeDirectionRef.current === -1 ? '#3b82f6' : 'transparent',
          borderRadius: `0 0 ${cfg.borderRadius}px ${cfg.borderRadius}px`,
          transition: isResizing ? 'none' : 'background-color 0.15s',
        }}
      />

      {/* ── Grip handle ── */}
      <div
        onMouseDown={handleGripMouseDown}
        onDoubleClick={handleGripDoubleClick}
        style={{
          cursor: isDragging.current ? 'grabbing' : 'grab',
          display: 'flex',
          alignItems: 'center',
          padding: '0 1px',
          color: '#9ca3af',
          flexShrink: 0,
        }}
        title={t('canvasToolbar.moveToolbar')}
      >
        <GripVertical size={cfg.gripSize} />
      </div>

      {/* ── Zoom (single button + popover) ── */}
      <ZoomPopoverButton
        cfg={cfg}
        adaptBtn={adaptBtn}
        zoomLabelRef={zoomLabelRef}
        onZoomIn={zoomIn}
        onZoomOut={zoomOut}
        onFitToContent={handleFitToContent}
      />
      <ToolbarTooltip label={t('canvasToolbar.rotate')}>
        <button
          onClick={() => handleRotate()}
          style={adaptBtn}
        >
          <RotateCw size={cfg.iconSize} />
        </button>
      </ToolbarTooltip>

      {/* ── Undo / Redo ── */}
      <ToolbarTooltip label={t('canvasToolbar.undo')}>
        <button
          onClick={undo}
          disabled={!canUndo}
          style={{
            ...adaptBtn,
            opacity: canUndo ? 1 : 0.35,
            cursor: canUndo ? 'pointer' : 'default',
          }}
        >
          <Undo2 size={cfg.iconSize} />
        </button>
      </ToolbarTooltip>
      <ToolbarTooltip label={t('canvasToolbar.redo')}>
        <button
          onClick={redo}
          disabled={!canRedo}
          style={{
            ...adaptBtn,
            opacity: canRedo ? 1 : 0.35,
            cursor: canRedo ? 'pointer' : 'default',
          }}
        >
          <Redo2 size={cfg.iconSize} />
        </button>
      </ToolbarTooltip>

      <ToolbarTooltip label={t('canvasToolbar.depExplorer')}>
        <button
          onClick={() => uiStore.send({ type: 'depExplorer.toggle' })}
          style={{
            ...adaptBtn,
            backgroundColor: isDepExplorerActive ? '#fffbeb' : '#f9fafb',
            borderColor: isDepExplorerActive ? '#f59e0b' : '#e5e7eb',
            color: isDepExplorerActive ? '#b45309' : '#374151',
          }}
        >
          <GitBranch size={cfg.iconSize} />
        </button>
      </ToolbarTooltip>

      <ToolbarTooltip label={t('canvasToolbar.spriteClickThrough')}>
        <button
          onClick={() => uiStore.send({ type: 'spriteClickThrough.toggle' })}
          style={{
            ...adaptBtn,
            backgroundColor: isSpriteClickThrough ? '#fffbeb' : '#f9fafb',
            borderColor: isSpriteClickThrough ? '#f59e0b' : '#e5e7eb',
            color: isSpriteClickThrough ? '#b45309' : '#374151',
          }}
        >
          <MousePointer2 size={cfg.iconSize} />
        </button>
      </ToolbarTooltip>

      {/* ── Add elements ── */}
      <ToolbarTooltip label={t('canvasToolbar.addElement')}>
        <button
          onClick={(e) => {
            const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
            uiStore.send({ type: 'contextMenu.set', menu: { x: rect.left, y: rect.top - 8, items: buildAddSubmenuItems() } });
          }}
          style={adaptBtn}
        >
          <Plus size={cfg.iconSize} />
        </button>
      </ToolbarTooltip>

      {/* ── Spacer before node-specific actions ── */}
      {(mergedActions.length > 0 || selectionCount > 0) && <div style={adaptDivider} />}

      {/* ── Toolbar Actions (entity properties) ── */}
      {mergedActions.length > 0 && (
        <ToolbarActionsSection actions={mergedActions} cfg={cfg} adaptBtn={adaptBtn} />
      )}

      {/* ── Selection actions ── */}
      {selectionCount > 0 && (
        <>
          <ToolbarTooltip label={t('canvasToolbar.removeNodes', { count: selectionCount })}>
            <button onClick={() => handleRemoveSelected()} style={adaptBtn}>
              <MinusCircle size={cfg.iconSize} />
            </button>
          </ToolbarTooltip>
          <span style={{
            fontSize: Math.max(7, cfg.btnSize * 0.45),
            fontWeight: 600,
            color: '#2563eb',
            backgroundColor: '#eff6ff',
            padding: `0 ${Math.max(3, cfg.px / 2)}px`,
            borderRadius: cfg.btnSize / 2,
            lineHeight: `${Math.max(12, cfg.btnSize * 0.75)}px`,
          }}>
            {selectionCount}
          </span>
        </>
      )}
    </div>
  );
}

// ══════════════════════════════════════════
// Zoom Popover Button
// ══════════════════════════════════════════

function ZoomPopoverButton({ cfg, adaptBtn, zoomLabelRef, onZoomIn, onZoomOut, onFitToContent }: {
  cfg: typeof STAGE_CONFIG[ToolbarStage];
  adaptBtn: React.CSSProperties;
  zoomLabelRef: React.RefObject<HTMLSpanElement | null>;
  onZoomIn: () => void;
  onZoomOut: () => void;
  onFitToContent: () => void;
}) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    function handleClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [open]);

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <ToolbarTooltip label={t('canvasToolbar.zoom')}>
        <button
          onClick={() => setOpen(o => !o)}
          style={{
            ...adaptBtn,
            backgroundColor: open ? '#eff6ff' : '#f9fafb',
            borderColor: open ? '#93c5fd' : '#e5e7eb',
          }}
        >
          <ZoomIn size={cfg.iconSize} />
        </button>
      </ToolbarTooltip>
      {open && (
        <div style={{
          position: 'absolute',
          bottom: '100%',
          left: '50%',
          transform: 'translateX(-50%)',
          marginBottom: 4,
          backgroundColor: '#fff',
          border: '1px solid #e5e7eb',
          borderRadius: 6,
          boxShadow: '0 4px 12px rgba(0,0,0,0.12)',
          zIndex: 10000,
          padding: 6,
          display: 'flex',
          alignItems: 'center',
          gap: 4,
        }}>
          <button onClick={onZoomOut} style={adaptBtn}>
            <ZoomOut size={cfg.iconSize} />
          </button>
          <span
            ref={zoomLabelRef}
            style={{
              fontSize: Math.max(9, cfg.iconSize - 1),
              color: '#6b7280',
              minWidth: Math.max(24, cfg.btnSize * 1.5),
              textAlign: 'center',
              fontVariantNumeric: 'tabular-nums',
            }}
          />
          <button onClick={onZoomIn} style={adaptBtn}>
            <ZoomIn size={cfg.iconSize} />
          </button>
          <div style={{ width: 1, height: cfg.dividerH, backgroundColor: '#e5e7eb', margin: '0 2px' }} />
          <ToolbarTooltip label={t('canvasToolbar.fitContent')}>
            <button onClick={onFitToContent} style={adaptBtn}>
              <Maximize2 size={cfg.iconSize} />
            </button>
          </ToolbarTooltip>
        </div>
      )}
    </div>
  );
}

// ══════════════════════════════════════════
// Toolbar Actions Section
// ══════════════════════════════════════════

interface ToolbarActionsSectionProps {
  actions: MergedToolbarAction[];
  cfg: typeof STAGE_CONFIG[ToolbarStage];
  adaptBtn: React.CSSProperties;
}

function ToolbarActionsSection({ actions, cfg, adaptBtn }: ToolbarActionsSectionProps) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: Math.max(2, cfg.gap) }}>
      {actions.map(action => {
        switch (action.type) {
          case 'color':
            return <ColorAction key={action.name} action={action} cfg={cfg} />;
          case 'select':
            return <SelectAction key={action.name} action={action} cfg={cfg} />;
          case 'toggle':
            return <ToggleAction key={action.name} action={action} cfg={cfg} adaptBtn={adaptBtn} />;
          default:
            return null;
        }
      })}
    </div>
  );
}

// ── Color Icon-Button + Sketch Picker Popover ──

const COLOR_ICONS: Record<string, typeof PaintBucket> = {
  backgroundColor: PaintBucket,
  textColor: Type,
  strokeColor: Palette,
  color: Palette,
  borderColor: Square,
};

function ColorAction({ action, cfg }: { action: MergedToolbarAction; cfg: typeof STAGE_CONFIG[ToolbarStage] }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const usedColors = useUsedColors();

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    function handleClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [open]);

  const Icon = COLOR_ICONS[action.name] ?? Palette;
  const barColor = action.commonValue !== undefined ? String(action.commonValue) : '#d1d5db';

  // Sketch preset = handler defaults + Tailwind palette (no used colors here, they go in their own section)
  const sketchPresetColors = useMemo(() => {
    const seen = new Set<string>();
    const result: string[] = [];
    const addColor = (c: string) => {
      const key = c.toLowerCase();
      if (!seen.has(key)) {
        seen.add(key);
        result.push(c);
      }
    };
    for (const opt of action.options) {
      if (opt.color) addColor(opt.color);
    }
    for (const c of TAILWIND_PALETTE) addColor(c);
    return result;
  }, [action.options]);

  // Group backgroundColor uses rgba => enable alpha for it
  const enableAlpha = action.name === 'backgroundColor' && action.options.some(o => o.color?.startsWith('rgba'));

  const currentColor = action.commonValue !== undefined ? String(action.commonValue) : '#000000';

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <ToolbarTooltip label={action.label}>
        <button
          onClick={() => setOpen(o => !o)}
          style={{
            height: cfg.btnSize,
            width: cfg.btnSize,
            padding: 0,
            border: '1px solid #e5e7eb',
            borderRadius: Math.max(3, cfg.btnSize / 6),
            cursor: 'pointer',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: open ? '#eff6ff' : '#f9fafb',
            borderColor: open ? '#93c5fd' : '#e5e7eb',
            flexShrink: 0,
            overflow: 'hidden',
            position: 'relative',
          }}
        >
          <Icon size={Math.max(8, cfg.iconSize - 2)} color="#374151" />
          {/* Color bar at bottom */}
          <div style={{
            position: 'absolute',
            bottom: 0,
            left: 2,
            right: 2,
            height: 3,
            backgroundColor: barColor,
            borderRadius: 1,
          }} />
        </button>
      </ToolbarTooltip>
      {open && (
        <div style={{
          position: 'absolute',
          bottom: '100%',
          left: '50%',
          transform: 'translateX(-50%)',
          marginBottom: 4,
          zIndex: 10000,
          display: 'flex',
          flexDirection: 'column',
          backgroundColor: '#fff',
          border: '1px solid #e5e7eb',
          borderRadius: 8,
          boxShadow: '0 4px 12px rgba(0,0,0,0.12)',
          overflow: 'hidden',
        }}>
          {/* ── Used Colors Section ── */}
          {usedColors.length > 0 && (
            <div style={{ padding: '8px 10px 4px' }}>
              <div style={{ fontSize: 10, fontWeight: 600, color: '#6b7280', marginBottom: 4, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                {t('canvasToolbar.usedColors')}
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 3 }}>
                {usedColors.map(c => {
                  const isActive = action.commonValue !== undefined && action.commonValue === c;
                  return (
                    <button
                      key={c}
                      onClick={() => action.applyAll(c)}
                      title={c}
                      style={{
                        width: 20,
                        height: 20,
                        borderRadius: 4,
                        border: isActive ? '2px solid #2563eb' : '1px solid #d1d5db',
                        backgroundColor: c,
                        cursor: 'pointer',
                        padding: 0,
                        flexShrink: 0,
                        boxShadow: isActive ? '0 0 0 1.5px #fff inset' : 'none',
                      }}
                    />
                  );
                })}
              </div>
            </div>
          )}
          {/* ── Sketch Picker ── */}
          <Sketch
            color={currentColor}
            disableAlpha={!enableAlpha}
            presetColors={sketchPresetColors}
            onChange={(color) => {
              if (enableAlpha && color.rgba.a !== undefined && color.rgba.a < 1) {
                action.applyAll(`rgba(${color.rgba.r}, ${color.rgba.g}, ${color.rgba.b}, ${color.rgba.a})`);
              } else {
                action.applyAll(color.hex);
              }
            }}
            style={{ boxShadow: 'none', border: 'none' }}
          />
        </div>
      )}
    </div>
  );
}

// ── Select Icon-Button + Popover ──

const SELECT_ICONS: Record<string, typeof Settings2> = {
  segmentType: Spline,
  displayMode: LayoutGrid,
  fontSize: ALargeSmall,
  fontWeight: Bold,
  strokeStyle: Minus,
  strokeWidth: Pencil,
  arrowStart: ArrowLeft,
  arrowEnd: ArrowRight,
  size: Maximize2,
  opacity: Eye,
  borderRadius: Square,
  borderWidth: Pencil,
};

/** SVG icon actions that render visual previews instead of text labels */
const VISUAL_SELECT_ACTIONS = new Set(['strokeStyle', 'strokeWidth', 'arrowStart', 'arrowEnd', 'segmentType', 'borderWidth']);

/** Renders an inline SVG preview for a strokeStyle option */
function StrokeStyleIcon({ id, width: w }: { id: string; width: number }) {
  const sw = 2;
  let dash: string | undefined;
  switch (id) {
    case 'longdash': dash = `${sw * 8} ${sw * 3}`; break;
    case 'dashed': dash = `${sw * 4} ${sw * 2}`; break;
    case 'shortdash': dash = `${sw * 2} ${sw * 2}`; break;
    case 'dotted': dash = `${sw} ${sw * 2}`; break;
    case 'dashdot': dash = `${sw * 4} ${sw * 2} ${sw} ${sw * 2}`; break;
    case 'longdashdotdot': dash = `${sw * 8} ${sw * 2} ${sw} ${sw * 2} ${sw} ${sw * 2}`; break;
  }
  return (
    <svg width={w} height={12} viewBox={`0 0 ${w} 12`}>
      <line x1={2} y1={6} x2={w - 2} y2={6} stroke="#374151" strokeWidth={sw} strokeDasharray={dash} strokeLinecap="round" />
    </svg>
  );
}

/** Renders an inline SVG preview for a strokeWidth option */
function StrokeWidthIcon({ width: w, strokeWidth }: { width: number; strokeWidth: number }) {
  const capped = Math.min(strokeWidth, 8);
  return (
    <svg width={w} height={Math.max(12, capped + 6)} viewBox={`0 0 ${w} ${Math.max(12, capped + 6)}`}>
      <line x1={2} y1={Math.max(6, (capped + 6) / 2)} x2={w - 2} y2={Math.max(6, (capped + 6) / 2)} stroke="#374151" strokeWidth={capped} strokeLinecap="round" />
    </svg>
  );
}

/** Renders an inline SVG preview for an arrowStart/arrowEnd option */
function ArrowTypeIcon({ id, end, width: w }: { id: string; end: 'start' | 'end'; width: number }) {
  const h = 14;
  const my = h / 2;
  const lineX1 = end === 'start' ? 14 : 2;
  const lineX2 = end === 'start' ? w - 2 : w - 14;
  const tipX = end === 'start' ? 4 : w - 4;
  const c = '#374151';

  return (
    <svg width={w} height={h} viewBox={`0 0 ${w} ${h}`}>
      {id === 'none' ? (
        <line x1={2} y1={my} x2={w - 2} y2={my} stroke={c} strokeWidth={1.5} strokeLinecap="round" />
      ) : (
        <line x1={lineX1} y1={my} x2={lineX2} y2={my} stroke={c} strokeWidth={1.5} strokeLinecap="round" />
      )}
      {id === 'arrow' && (
        <polyline
          points={end === 'start'
            ? `${tipX + 7},${my - 4} ${tipX},${my} ${tipX + 7},${my + 4}`
            : `${tipX - 7},${my - 4} ${tipX},${my} ${tipX - 7},${my + 4}`}
          stroke={c} strokeWidth={1.5} fill={c} strokeLinejoin="round" strokeLinecap="round"
        />
      )}
      {id === 'open-arrow' && (
        <polyline
          points={end === 'start'
            ? `${tipX + 7},${my - 4} ${tipX},${my} ${tipX + 7},${my + 4}`
            : `${tipX - 7},${my - 4} ${tipX},${my} ${tipX - 7},${my + 4}`}
          stroke={c} strokeWidth={1.5} fill="none" strokeLinejoin="round" strokeLinecap="round"
        />
      )}
      {id === 'triangle' && (
        <polygon
          points={end === 'start'
            ? `${tipX},${my} ${tipX + 8},${my - 5} ${tipX + 8},${my + 5}`
            : `${tipX},${my} ${tipX - 8},${my - 5} ${tipX - 8},${my + 5}`}
          stroke={c} strokeWidth={0.5} fill={c}
        />
      )}
      {id === 'diamond' && (
        <polygon
          points={`${tipX},${my - 4} ${tipX + 5},${my} ${tipX},${my + 4} ${tipX - 5},${my}`}
          stroke={c} strokeWidth={1} fill={c}
        />
      )}
      {id === 'circle' && (
        <circle cx={tipX} cy={my} r={3.5} stroke={c} strokeWidth={1} fill={c} />
      )}
      {id === 'bar' && (
        <line x1={tipX} y1={my - 5} x2={tipX} y2={my + 5} stroke={c} strokeWidth={2} strokeLinecap="round" />
      )}
      {id === 'square' && (
        <rect x={tipX - 3} y={my - 3} width={6} height={6} fill={c} />
      )}
    </svg>
  );
}

/** Renders an inline SVG preview for a segmentType option */
function SegmentTypeIcon({ id, width: w }: { id: string; width: number }) {
  const h = 16;
  return (
    <svg width={w} height={h} viewBox={`0 0 ${w} ${h}`}>
      {id === 'bezier' && (
        <path d={`M 2,${h - 3} C ${w * 0.3},${h - 3} ${w * 0.7},3 ${w - 2},3`} stroke="#374151" strokeWidth={1.5} fill="none" strokeLinecap="round" />
      )}
      {id === 'straight' && (
        <line x1={2} y1={h - 3} x2={w - 2} y2={3} stroke="#374151" strokeWidth={1.5} strokeLinecap="round" />
      )}
      {id === 'ortho90' && (
        <polyline points={`2,${h - 3} 2,3 ${w - 2},3`} stroke="#374151" strokeWidth={1.5} fill="none" strokeLinejoin="round" strokeLinecap="round" />
      )}
      {id === 'angle45' && (
        <polyline points={`2,${h - 3} ${(h - 6) + 2},3 ${w - 2},3`} stroke="#374151" strokeWidth={1.5} fill="none" strokeLinejoin="round" strokeLinecap="round" />
      )}
      {id === 'elbowed' && (
        <polyline points={`2,3 8,3 8,${h - 3} ${w - 2},${h - 3}`} stroke="#374151" strokeWidth={1.5} fill="none" strokeLinejoin="round" strokeLinecap="round" />
      )}
    </svg>
  );
}

function SelectAction({ action, cfg }: { action: MergedToolbarAction; cfg: typeof STAGE_CONFIG[ToolbarStage] }) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const [customInputValue, setCustomInputValue] = useState('');

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    function handleClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [open]);

  // Reset custom input value when popover opens
  useEffect(() => {
    if (open && action.allowCustomInput) {
      setCustomInputValue(action.commonValue !== undefined ? String(action.commonValue) : '');
    }
  }, [open, action.allowCustomInput, action.commonValue]);

  const currentLabel = action.commonValue !== undefined
    ? action.options.find(o => o.id === action.commonValue)?.label ?? String(action.commonValue)
    : '\u2014';

  const Icon = SELECT_ICONS[action.name] ?? Settings2;
  const isVisual = VISUAL_SELECT_ACTIONS.has(action.name);

  const applyCustomInput = () => {
    const num = parseFloat(customInputValue);
    if (!isNaN(num)) {
      const min = action.customInputMin ?? 0;
      const max = action.customInputMax ?? Infinity;
      const clamped = Math.max(min, Math.min(max, num));
      action.applyAll(String(clamped));
    }
  };

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <ToolbarTooltip label={`${action.label}: ${currentLabel}`}>
        <button
          onClick={() => setOpen(o => !o)}
          style={{
            height: cfg.btnSize,
            width: cfg.btnSize,
            padding: 0,
            border: '1px solid #e5e7eb',
            borderRadius: Math.max(3, cfg.btnSize / 6),
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: open ? '#eff6ff' : '#f9fafb',
            borderColor: open ? '#93c5fd' : '#e5e7eb',
            color: '#374151',
            flexShrink: 0,
          }}
        >
          <Icon size={Math.max(8, cfg.iconSize - 2)} />
        </button>
      </ToolbarTooltip>
      {open && (
        <div style={{
          position: 'absolute',
          bottom: '100%',
          left: '50%',
          transform: 'translateX(-50%)',
          marginBottom: 4,
          backgroundColor: '#fff',
          border: '1px solid #e5e7eb',
          borderRadius: 8,
          boxShadow: '0 4px 12px rgba(0,0,0,0.12)',
          zIndex: 10000,
          minWidth: isVisual ? 140 : 80,
          overflow: 'hidden',
          padding: isVisual ? 4 : 0,
        }}>
          {/* Header for visual actions */}
          {isVisual && (
            <div style={{ fontSize: 10, fontWeight: 600, color: '#6b7280', padding: '2px 6px 4px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
              {action.label}
            </div>
          )}
          {isVisual ? (
            <div style={{
              display: (action.name === 'strokeWidth' || action.name === 'borderWidth') ? 'grid' : 'flex',
              flexDirection: 'column',
              gap: 2,
              ...((action.name === 'strokeWidth' || action.name === 'borderWidth') ? { gridTemplateColumns: 'repeat(2, 1fr)' } : {}),
            }}>
              {action.options.map(opt => {
                const isActive = action.commonValue === opt.id;
                const previewW = (action.name === 'strokeWidth' || action.name === 'borderWidth') ? 52 : 80;
                return (
                  <button
                    key={opt.id}
                    onClick={() => { action.applyAll(opt.id); setOpen(false); }}
                    title={opt.label}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 6,
                      padding: '4px 6px',
                      border: isActive ? '1.5px solid #2563eb' : '1px solid transparent',
                      borderRadius: 5,
                      backgroundColor: isActive ? '#eff6ff' : 'transparent',
                      cursor: 'pointer',
                      whiteSpace: 'nowrap',
                    }}
                    onMouseEnter={e => { if (!isActive) (e.currentTarget as HTMLElement).style.backgroundColor = '#f3f4f6'; }}
                    onMouseLeave={e => { if (!isActive) (e.currentTarget as HTMLElement).style.backgroundColor = 'transparent'; }}
                  >
                    {action.name === 'strokeStyle' && <StrokeStyleIcon id={opt.id} width={previewW} />}
                    {action.name === 'strokeWidth' && <StrokeWidthIcon width={previewW} strokeWidth={Number(opt.id)} />}
                    {action.name === 'borderWidth' && (opt.id === 'none'
                      ? <svg width={previewW} height={12} viewBox={`0 0 ${previewW} 12`}><line x1={2} y1={6} x2={previewW - 2} y2={6} stroke="#d1d5db" strokeWidth={1} strokeDasharray="3 3" strokeLinecap="round" /></svg>
                      : <StrokeWidthIcon width={previewW} strokeWidth={Number(opt.id)} />)}
                    {action.name === 'arrowStart' && <ArrowTypeIcon id={opt.id} end="start" width={previewW} />}
                    {action.name === 'arrowEnd' && <ArrowTypeIcon id={opt.id} end="end" width={previewW} />}
                    {action.name === 'segmentType' && <SegmentTypeIcon id={opt.id} width={previewW} />}
                    <span style={{
                      fontSize: 10,
                      color: isActive ? '#1d4ed8' : '#6b7280',
                      fontWeight: isActive ? 600 : 400,
                    }}>
                      {opt.label}
                    </span>
                  </button>
                );
              })}
            </div>
          ) : (
            /* Text-only fallback for non-visual selects */
            action.options.map(opt => (
              <button
                key={opt.id}
                onClick={() => { action.applyAll(opt.id); setOpen(false); }}
                style={{
                  display: 'block',
                  width: '100%',
                  padding: '4px 10px',
                  border: 'none',
                  backgroundColor: action.commonValue === opt.id ? '#eff6ff' : 'transparent',
                  color: '#374151',
                  fontSize: Math.max(10, cfg.iconSize),
                  textAlign: 'left',
                  cursor: 'pointer',
                  fontWeight: action.commonValue === opt.id ? 600 : 400,
                  whiteSpace: 'nowrap',
                }}
                onMouseEnter={e => { (e.currentTarget as HTMLElement).style.backgroundColor = '#f3f4f6'; }}
                onMouseLeave={e => { (e.currentTarget as HTMLElement).style.backgroundColor = action.commonValue === opt.id ? '#eff6ff' : 'transparent'; }}
              >
                {opt.label}
              </button>
            ))
          )}
          {/* Custom number input */}
          {action.allowCustomInput && (
            <div style={{ borderTop: '1px solid #e5e7eb', padding: '6px 8px', display: 'flex', alignItems: 'center', gap: 4 }}>
              <input
                type="number"
                value={customInputValue}
                onChange={e => setCustomInputValue(e.target.value)}
                onKeyDown={e => {
                  if (e.key === 'Enter') { applyCustomInput(); setOpen(false); }
                  if (e.key === 'Escape') setOpen(false);
                }}
                onBlur={applyCustomInput}
                min={action.customInputMin}
                max={action.customInputMax}
                step={action.customInputStep}
                placeholder={action.commonValue !== undefined ? String(action.commonValue) : ''}
                style={{
                  width: 56,
                  height: 24,
                  padding: '0 4px',
                  fontSize: 11,
                  border: '1px solid #d1d5db',
                  borderRadius: 4,
                  outline: 'none',
                  backgroundColor: '#fff',
                  color: '#374151',
                  textAlign: 'right',
                }}
              />
              {action.customInputUnit && (
                <span style={{ fontSize: 10, color: '#6b7280' }}>{action.customInputUnit}</span>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ── Toggle Button ──

const TOGGLE_ICONS: Record<string, { on: typeof Link; off: typeof Unlink }> = {
  fixedStroke: { on: Lock, off: Unlock },
};

function ToggleAction({ action, cfg, adaptBtn }: { action: MergedToolbarAction; cfg: typeof STAGE_CONFIG[ToolbarStage]; adaptBtn: React.CSSProperties }) {
  const { t } = useTranslation();
  const isMixed = action.commonValue === undefined;
  const isOn = !isMixed && !!action.commonValue;
  const icons = TOGGLE_ICONS[action.name];
  const OnIcon = icons?.on ?? Link;
  const OffIcon = icons?.off ?? Unlink;

  return (
    <ToolbarTooltip label={`${action.label}: ${isMixed ? t('canvasToolbar.mixed') : isOn ? t('canvasToolbar.on') : t('canvasToolbar.off')}`}>
      <button
        onClick={() => action.applyAll(!isOn)}
        style={{
          ...adaptBtn,
          color: isMixed ? '#9ca3af' : isOn ? '#2563eb' : '#9ca3af',
          opacity: isMixed ? 0.6 : 1,
          backgroundColor: isOn ? '#eff6ff' : '#f9fafb',
          borderColor: isOn ? '#93c5fd' : '#e5e7eb',
        }}
      >
        {isOn ? <OnIcon size={cfg.iconSize} /> : <OffIcon size={cfg.iconSize} />}
      </button>
    </ToolbarTooltip>
  );
}
